import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { createModule, CreateModuleRequest } from '../../services/lessonService';

const SUBJECTS = [
  'Math',
  'Science',
  'English',
  'History',
  'Social Studies',
  'Art',
  'Music',
  'Physical Education',
  'Computer Science',
  'Foreign Language',
];

const GRADE_LEVELS = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

const steps = ['Module Information', 'Learning Objectives', 'Review & Create'];

export const CreateLesson: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [subject, setSubject] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [durationHours, setDurationHours] = useState(3);
  const [learningObjectives, setLearningObjectives] = useState<string[]>(['']);
  const [prerequisites, setPrerequisites] = useState('');
  const [standards, setStandards] = useState<string[]>([]);
  const [standardInput, setStandardInput] = useState('');

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const addLearningObjective = () => {
    setLearningObjectives([...learningObjectives, '']);
  };

  const updateLearningObjective = (index: number, value: string) => {
    const newObjectives = [...learningObjectives];
    newObjectives[index] = value;
    setLearningObjectives(newObjectives);
  };

  const removeLearningObjective = (index: number) => {
    const newObjectives = learningObjectives.filter((_, i) => i !== index);
    setLearningObjectives(newObjectives);
  };

  const addStandard = () => {
    if (standardInput.trim()) {
      setStandards([...standards, standardInput.trim()]);
      setStandardInput('');
    }
  };

  const removeStandard = (index: number) => {
    setStandards(standards.filter((_, i) => i !== index));
  };

  const handleCreateModule = async () => {
    setLoading(true);
    setError(null);

    try {
      const moduleData: CreateModuleRequest = {
        title,
        subject,
        grade_level: gradeLevel,
        duration_hours: durationHours,
        learning_objectives: learningObjectives.filter((obj) => obj.trim() !== ''),
        prerequisites: prerequisites.trim() ? [prerequisites] : undefined,
        standards: standards.length > 0 ? standards : undefined,
      };

      const module = await createModule(moduleData);
      navigate(`/teacher/lessons/${module.module_id}/units`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create module');
    } finally {
      setLoading(false);
    }
  };

  const isStepValid = () => {
    switch (activeStep) {
      case 0:
        return title.trim() !== '' && subject !== '' && gradeLevel !== '' && durationHours > 0;
      case 1:
        return learningObjectives.some((obj) => obj.trim() !== '');
      case 2:
        return true;
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Module Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              placeholder="e.g., Introduction to Photosynthesis"
            />

            <TextField
              select
              label="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              fullWidth
              required
            >
              {SUBJECTS.map((subj) => (
                <MenuItem key={subj} value={subj}>
                  {subj}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Grade Level"
              value={gradeLevel}
              onChange={(e) => setGradeLevel(e.target.value)}
              fullWidth
              required
            >
              {GRADE_LEVELS.map((grade) => (
                <MenuItem key={grade} value={grade}>
                  Grade {grade}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Duration (hours)"
              type="number"
              value={durationHours}
              onChange={(e) => setDurationHours(Number(e.target.value))}
              fullWidth
              required
              inputProps={{ min: 0.5, step: 0.5 }}
            />

            <TextField
              label="Prerequisites (Optional)"
              value={prerequisites}
              onChange={(e) => setPrerequisites(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="What should students know before starting this module?"
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Standards Alignment (Optional)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  size="small"
                  value={standardInput}
                  onChange={(e) => setStandardInput(e.target.value)}
                  placeholder="e.g., NGSS.MS-LS1-6"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addStandard();
                    }
                  }}
                  fullWidth
                />
                <Button onClick={addStandard} variant="outlined">
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {standards.map((standard, index) => (
                  <Chip key={index} label={standard} onDelete={() => removeStandard(index)} />
                ))}
              </Box>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">Learning Objectives</Typography>
            <Typography variant="body2" color="text.secondary">
              What will students be able to do after completing this module?
            </Typography>

            {learningObjectives.map((objective, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label={`Objective ${index + 1}`}
                  value={objective}
                  onChange={(e) => updateLearningObjective(index, e.target.value)}
                  fullWidth
                  placeholder="e.g., Understand the role of light in photosynthesis"
                />
                {learningObjectives.length > 1 && (
                  <IconButton onClick={() => removeLearningObjective(index)} color="error">
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={addLearningObjective}
              variant="outlined"
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Objective
            </Button>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">Review Your Module</Typography>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Title
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {title}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                  Subject & Grade Level
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {subject} - Grade {gradeLevel}
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                  Duration
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {durationHours} hours
                </Typography>

                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                  Learning Objectives
                </Typography>
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  {learningObjectives
                    .filter((obj) => obj.trim() !== '')
                    .map((objective, index) => (
                      <li key={index}>
                        <Typography variant="body2">{objective}</Typography>
                      </li>
                    ))}
                </Box>

                {prerequisites && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                      Prerequisites
                    </Typography>
                    <Typography variant="body2">{prerequisites}</Typography>
                  </>
                )}

                {standards.length > 0 && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                      Standards
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {standards.map((standard, index) => (
                        <Chip key={index} label={standard} size="small" />
                      ))}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Create New Lesson Module
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {renderStepContent()}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Back
            </Button>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={() => navigate('/teacher/lessons')}>
                Cancel
              </Button>

              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleCreateModule}
                  disabled={!isStepValid() || loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Create Module'}
                </Button>
              ) : (
                <Button variant="contained" onClick={handleNext} disabled={!isStepValid()}>
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
